/**
 * Site Control — plot / trade hand-over sign-off DTOs.
 *
 * Mirrors Prisma `Plot` (prisma/schema.prisma), the global `Trade` library and
 * the `TradeSignOff` junction. The board and drill-in shapes mirror what
 * `lib/plots/plot-status.ts` computes on READ (`computeBoardSummary` /
 * `loadPlotTradeRows`): "signed" is recomputed from the raw signals under the
 * project's `plotSignOffMode`, never read off the denormalised
 * `TradeSignOff.status`.
 *
 * Validation mirrors `lib/plots/plot-input.ts` — `PLOT_BULK_MAX` is the single
 * source for the bulk ceiling (that module re-exports it as `MAX_BULK_PLOTS`),
 * so the wire cap and the server cap cannot drift.
 *
 * Ids are uuid() per the Prisma models (Trade is cuid()); `entityIdSchema`
 * accepts both plus the readable seed ids.
 */
import { z } from "zod";
import {
  INT32_MAX,
  INT32_MIN,
  entityIdSchema,
  orderIndexSchema,
} from "../common";

/** Prisma enum `PlotSignOffMode` — how a (plot, trade) slot may be completed. */
export const PLOT_SIGN_OFF_MODES = [
  "SIGNED_DOCUMENT",
  "MANUAL_TICK",
  "EITHER",
] as const;

export type PlotSignOffMode = (typeof PLOT_SIGN_OFF_MODES)[number];

export const PlotSignOffModeSchema = z.enum(PLOT_SIGN_OFF_MODES);

/**
 * Hard ceiling on a single bulk-create request (well above a ~55-plot site).
 * THE source of the limit: `lib/plots/plot-input.ts` re-exports this value as
 * `MAX_BULK_PLOTS` rather than declaring its own, so the client-side wire
 * bound and the server-side generator bound are the same number by
 * construction.
 */
export const PLOT_BULK_MAX = 500;

/**
 * Cumulative ceiling on the plots ONE project may hold.
 *
 * A DISTINCT concept from `PLOT_BULK_MAX` (which bounds a single request) that
 * happens to carry the same value today: without it, `PLOT_BULK_MAX` bounds
 * only each call, so repeated bulk requests grow a project without limit and
 * the board — deliberately unpaginated, web parity — has no bound at all.
 * Named separately so the two can diverge without hunting for which 500 meant
 * what; 500 is far above the "~55 plots per development" the schema documents.
 * Enforced inside the creation transaction in `lib/plots/plot-service.ts`, so
 * web and mobile share one cap.
 */
export const PLOT_PROJECT_MAX = 500;

/** `Plot.reference` / `Plot.name` column bounds (web parity). */
export const PLOT_REFERENCE_MAX = 120;
export const PLOT_NAME_MAX = 200;
/** `prefix` / `start` bounds on the generated-range form (web parity). */
export const PLOT_BULK_PREFIX_MAX = 40;
export const PLOT_BULK_START_MAX = 100_000;

/**
 * Plot reference normalisation — trim, then collapse internal whitespace.
 *
 * Lives HERE, not in `lib/`, so the WIRE bound and the STORED value are checked
 * against the same string: `PlotReferenceSchema` below measures the normalised
 * form, and `lib/plots/plot-input.ts` re-exports this function as
 * `normalizePlotReference` rather than keeping a second copy. Casing is
 * preserved deliberately — the developer's own label is what the board shows.
 */
export function normalizePlotReference(raw: string): string {
  return raw.trim().replace(/\s+/g, " ");
}

/**
 * A single plot reference on the wire, bounded by what will actually be
 * STORED. Measuring the raw string would reject "Plot   12" padded to 121
 * characters that normalises to 9, and — worse — accept nothing at all in the
 * bulk `references[]`, which carried no per-element bound before (#2334
 * round 2).
 */
export const PlotReferenceSchema = z
  .string()
  .min(1)
  .refine((value) => normalizePlotReference(value).length >= 1, {
    message: "A plot reference is required.",
  })
  .refine(
    (value) => normalizePlotReference(value).length <= PLOT_REFERENCE_MAX,
    {
      message: `A plot reference must be ${PLOT_REFERENCE_MAX} characters or fewer.`,
    },
  );

export const PlotSchema = z
  .object({
    id: entityIdSchema,
    projectId: entityIdSchema,
    /** Developer-typed label, e.g. "Plot 12". Unique per project. */
    reference: z.string(),
    name: z.string().nullable(),
    // Output DTO mirrors the Prisma `Int` column's full range, never the
    // narrower client-input cap — a read-side schema must accept every value
    // the column can hold (#2378/#2334).
    orderIndex: z.number().int().min(INT32_MIN).max(INT32_MAX),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  });

export type Plot = z.infer<typeof PlotSchema>;

/** The plot identity carried alongside the drill-in rows. */
export const PlotSummarySchema = z
  .object({
    id: entityIdSchema,
    reference: z.string(),
    name: z.string().nullable(),
  });

export type PlotSummary = z.infer<typeof PlotSummarySchema>;

/** One board tile: "N of <active trades> signed" for a single plot. */
export const PlotProgressSchema = z
  .object({
    id: entityIdSchema,
    reference: z.string(),
    name: z.string().nullable(),
    signed: z.number().int().min(0),
    total: z.number().int().min(0),
    complete: z.boolean(),
  });

export type PlotProgress = z.infer<typeof PlotProgressSchema>;

/**
 * GET /api/mobile/v1/projects/:projectId/plots
 *
 * `manualSignOffAllowed` is the SERVER's answer to "may this client offer
 * Mark signed / Unmark", derived from `plotSignOffMode` at the same place the
 * write routes enforce it — so the control and the refusal can never disagree
 * (the web board derives the same boolean inline).
 */
export const PlotBoardSchema = z
  .object({
    projectId: entityIdSchema,
    plotSignOffMode: PlotSignOffModeSchema,
    manualSignOffAllowed: z.boolean(),
    plots: z.array(PlotProgressSchema),
    totalPlots: z.number().int().min(0),
    completePlots: z.number().int().min(0),
    totalTrades: z.number().int().min(0),
  });

export type PlotBoard = z.infer<typeof PlotBoardSchema>;

/** How a completed slot was completed. Null while the trade is waiting. */
export const PLOT_TRADE_SIGN_OFF_VIA = ["manual", "document"] as const;

export const PlotTradeRowSchema = z
  .object({
    tradeId: entityIdSchema,
    tradeName: z.string(),
    signed: z.boolean(),
    signedAt: z.string().datetime().nullable(),
    via: z.enum(PLOT_TRADE_SIGN_OFF_VIA).nullable(),
    markedManually: z.boolean(),
    documentVersionId: entityIdSchema.nullable(),
  });

export type PlotTradeRow = z.infer<typeof PlotTradeRowSchema>;

/**
 * GET /api/mobile/v1/projects/:projectId/plots/:plotId — one row per ACTIVE
 * trade (the derived checklist). Also the response of the manual mark / unmark
 * endpoints, so a client writes the recomputed state straight back into its
 * cache instead of refetching (the web equivalent is `router.refresh()`).
 */
export const PlotTradeRowsSchema = z
  .object({
    projectId: entityIdSchema,
    plotSignOffMode: PlotSignOffModeSchema,
    manualSignOffAllowed: z.boolean(),
    plot: PlotSummarySchema,
    rows: z.array(PlotTradeRowSchema),
    signed: z.number().int().min(0),
    total: z.number().int().min(0),
    complete: z.boolean(),
  });

export type PlotTradeRows = z.infer<typeof PlotTradeRowsSchema>;

/** POST /api/mobile/v1/projects/:projectId/plots */
export const PlotCreateRequest = z
  .object({
    reference: PlotReferenceSchema,
    name: z.string().max(PLOT_NAME_MAX).nullable().optional(),
  })
  .strict();

export type PlotCreateRequest = z.infer<typeof PlotCreateRequest>;

/**
 * PATCH /api/mobile/v1/projects/:projectId/plots/:plotId
 *
 * Every field is optional and only the keys PRESENT in the request are
 * applied — an omitted `name` leaves the stored value alone, while an
 * explicit `null` clears it.
 */
export const PlotUpdateRequest = z
  .object({
    reference: PlotReferenceSchema.optional(),
    name: z.string().max(PLOT_NAME_MAX).nullable().optional(),
    orderIndex: orderIndexSchema.optional(),
  })
  .strict();

export type PlotUpdateRequest = z.infer<typeof PlotUpdateRequest>;

/**
 * POST /api/mobile/v1/projects/:projectId/plots/bulk — either an explicit
 * `references[]` or a generated `prefix`+`start`+`count` range.
 */
export const PlotBulkCreateRequest = z
  .object({
    // Per-element bound as well as the list bound: an unbounded element let a
    // caller store an arbitrarily long reference through the bulk path while
    // the single-create path rejected it (#2334 round 2).
    references: z.array(PlotReferenceSchema).max(PLOT_BULK_MAX).optional(),
    prefix: z.string().max(PLOT_BULK_PREFIX_MAX).optional(),
    start: z.number().int().min(0).max(PLOT_BULK_START_MAX).optional(),
    count: z.number().int().min(1).max(PLOT_BULK_MAX).optional(),
  })
  .strict()
  .refine(
    (d) =>
      (d.references !== undefined && d.references.length > 0) ||
      (d.count !== undefined && d.count > 0),
    { message: "Provide either a non-empty references[] or a count." },
  );

export type PlotBulkCreateRequest = z.infer<typeof PlotBulkCreateRequest>;

/**
 * Bulk create is idempotent: references colliding with existing plots are
 * skipped, so `created + skipped === requested` and a replay creates nothing.
 */
export const PlotBulkCreateResponse = z
  .object({
    requested: z.number().int().min(0),
    created: z.number().int().min(0),
    skipped: z.number().int().min(0),
  });

export type PlotBulkCreateResponse = z.infer<typeof PlotBulkCreateResponse>;

/** DELETE /api/mobile/v1/projects/:projectId/plots/:plotId */
export const PlotDeleteResponse = z.object({ ok: z.literal(true) });

export type PlotDeleteResponse = z.infer<typeof PlotDeleteResponse>;

/** Full-depth strict variants used only at server response boundaries. */
export const PlotStrict = PlotSchema.strict();
export const PlotSummaryStrict = PlotSummarySchema.strict();
export const PlotProgressStrict = PlotProgressSchema.strict();
export const PlotBoardStrict = PlotBoardSchema.extend({
  plots: z.array(PlotProgressStrict),
}).strict();
export const PlotTradeRowStrict = PlotTradeRowSchema.strict();
export const PlotTradeRowsStrict = PlotTradeRowsSchema.extend({
  plot: PlotSummaryStrict,
  rows: z.array(PlotTradeRowStrict),
}).strict();
export const PlotBulkCreateResponseStrict = PlotBulkCreateResponse.strict();
export const PlotDeleteResponseStrict = PlotDeleteResponse.strict();
